import PropTypes from "prop-types"
import classNames from "classnames"
import { isObject, map, get, keys, every, isEmpty, isNumber, isNil } from "lodash"
import { Switch, TextField, InputAdornment, FormControlLabel, Button, Paper } from "@mui/material"
import Tooltip from "components/material/tooltip"
import { InfoOutlined } from "@mui/icons-material"
import { UI_CONSTRAINT } from "../../shapes"
import "./style.scss"

export default function Constraints({ uiConstraints, computedConstraints, setConstraint, applyButtonEnabled, applyConstraints, constraintsErrors, portfolioType, reversedActions }) {
  const onSettingSelected = event => {
    const { id, checked } = event.target

    if (checked) {
      setConstraint(id, uiConstraints[id].value || uiConstraints[id].defaultValue)
    } else {
      setConstraint(id, null)
    }
  }

  const onSettingChanged = event => {
    const { name, value } = event.target

    setConstraint(name, value)
  }

  const onApply = () => {
    applyConstraints("general", computedConstraints)
  }

  const onFocus = event => {
    event.target.select()
  }

  const maxTradeSize = (constraint, id) => {
    const { value: values, validation, unit: units, tooltip: tooltips, step, min, max } = constraint
    const names = keys(values)

    const fieldValues = []

    const nodes = map(names, (key, i) => {
      const value = values[key]
      const unit = units[key]
      const tooltip = tooltips[key]
      const error = validation(value, unit)
      fieldValues.push(value)

      return <TextField
        variant="standard"
        key={key}
        name={id}
        value={value}
        onChange={(e) => onSettingChanged({ target: { name: id, value: { ...values, [key]: get(e, "target.value") } } })}
        onFocus={onFocus}
        autoFocus={i === 0}
        error={!!error}
        helperText={error}
        color="secondary"
        type="number"
        slotProps={{
          input: {
            startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">{unit}</InputAdornment>,
            endAdornment: tooltip && <InputAdornment style={{ whiteSpace: "nowrap" }} position="end"><Tooltip title={tooltip}><InfoOutlined /></Tooltip></InputAdornment>,
          },

          htmlInput: {
            step: step || 1,
            min,
            max,
          },
        }}
      />
    })
    // we only care about the all fields are empty error because the others are already handle per field
    return [
      nodes,
      every(fieldValues, v => !isNumber(v) && isEmpty(v)) ? "At least one value must be entered" : false,
    ]
  }
  const renderConstraint = (constraint, id) => {
    const { value, isBoolean } = constraint
    if (isNil(value) || isBoolean) { return [] }

    if (isObject(value)) {
      return maxTradeSize(constraint, id)
    }
    const { secondField, helperText, secondHelperText, unit, step, min, max, tooltip, error } = constraint
    const endAdornment = tooltip ? <InputAdornment style={{ whiteSpace: "nowrap" }} position="end"><Tooltip title={tooltip}><InfoOutlined /></Tooltip></InputAdornment> : null
    return [<>
      <TextField
        variant="standard"
        name={id}
        value={value}
        onChange={onSettingChanged}
        onFocus={onFocus}
        autoFocus
        error={!!error}
        color="secondary"
        type="number"
        helperText={error || helperText}
        slotProps={{
          input: {
            startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">{unit}</InputAdornment>,
            endAdornment,
          },

          htmlInput: {
            step: step || 1,
            min,
            max,
          },
        }}
      />

      {secondField && <TextField
        variant="standard"
        color="secondary"
        value={secondField(value)}
        helperText={secondHelperText}
        disabled
        slotProps={{
          input: {
            startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">{unit}</InputAdornment>,
          },
        }}
      />}
    </>]
  }
  let emptyError = false
  return <div>
    <Paper square className="constraints-wrapper">
      {map(uiConstraints, (constraint, id) => {
        const { label, selected, disabled, tooltip, isBoolean } = constraint
        const [field, empty] = renderConstraint(constraint, id)
        emptyError = empty

        return <div className="option-line" key={id}>
          <FormControlLabel
            title={disabled && disabled({ portfolioType }) ? `This portfolio is ${portfolioType === "LONG_ONLY" ? "100%" : "0%"} Net Long and cannot add ${portfolioType === "LONG_ONLY" ? "shorts" : "longs"}.` : ""}
            control={
              <Switch
                checked={selected}
                id={id}
                disabled={disabled && disabled({ portfolioType })}
                onChange={onSettingSelected}
              />
            }
            label={label}
          />
          {isBoolean && <Tooltip title={tooltip}><InfoOutlined /></Tooltip>}
          {selected && field}
          {emptyError && <div className="option-error">{emptyError}</div>}
        </div>
      })}
    </Paper>
    <div className={classNames("buttons-bar", { reversed: reversedActions })}>
      <Button variant="contained" color="primary" onClick={onApply} disabled={!applyButtonEnabled || constraintsErrors || emptyError}>Apply</Button>
    </div>
  </div>
}

Constraints.propTypes = {
  uiConstraints: PropTypes.objectOf(UI_CONSTRAINT).isRequired,
  computedConstraints: PropTypes.object.isRequired,
  setConstraint: PropTypes.func.isRequired,
  applyButtonEnabled: PropTypes.bool.isRequired,
  applyConstraints: PropTypes.func.isRequired,
  constraintsErrors: PropTypes.bool.isRequired,
  portfolioType: PropTypes.string.isRequired,
  reversedActions: PropTypes.bool,
}
